import { Grid, Typography, Button, Select, MenuItem, FormControl, InputLabel, Modal } from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import { ACL, useDeleteUser, useListGroups, useListUsers, User, useUpdateUser } from "../hooks/useAdmin";
import { useMemo, useState } from "react";
import { ConfirmModal } from "./Modals/ConfirmModal";
import { SetUserPasswordModal } from "./Modals/SetUserPasswordModal";


enum ModalState {
  CONFIRM_UPDATE_GROUP,
  SET_PASSWORD,
  CONFIRM_USER_DELETE,
}

export const AdminUsers = () => {
  const { data: users, isLoading: usersLoading, refetch: refetchUsers } = useListUsers();
  const { data: groups, isLoading: groupsLoading } = useListGroups();
  const { put: updateUser, isLoading: updateUserLoading } = useUpdateUser();
  const { del: deleteUser } = useDeleteUser();
  const [selectedUser, setSelectedUser] = useState<User | undefined>();
  const [modalState, setModalState] = useState<ModalState | undefined>(undefined);
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>(undefined);

  const confirmModalDetails = useMemo((): { title: string, message: string, onConfirm: () => void, onCancel: () => void } => {
    if (modalState === ModalState.CONFIRM_UPDATE_GROUP) {
      return {
        title: "Change Group",
        message: "Are you sure you want to change this user's group?",
        onCancel() {
          setModalState(undefined);
        },
        onConfirm: async () => {
          if (selectedUser) {
            await updateUser(`/admin/user/${selectedUser.id}`, { groupId: selectedGroupId });
            await refetchUsers();
            setSelectedUser(undefined);
          }
          setModalState(undefined);
          setSelectedUser(undefined);
          setSelectedGroupId(undefined);
        },
      }
    } else if (modalState === ModalState.CONFIRM_USER_DELETE) {
      return {
        title: "Delete User",
        message: "Are you sure you want to delete this user?",
        onCancel() {
          setModalState(undefined);
        },
        onConfirm: async () => {
          if (selectedUser) {
            await deleteUser(`/admin/user/${selectedUser.id}`);
            await refetchUsers();
            setSelectedUser(undefined);
          }
          setModalState(undefined);
          setSelectedUser(undefined);
          setSelectedGroupId(undefined);
        },
      }
    }

    return {
      title: "foo",
      message: "bar",
      onCancel() {

      },
      onConfirm() {

      },
    };
  }, [modalState, setModalState]);

  return (
    <Grid size={11} spacing={2} container>
      <ConfirmModal
        open={modalState === ModalState.CONFIRM_UPDATE_GROUP || modalState === ModalState.CONFIRM_USER_DELETE}
        onCancel={confirmModalDetails.onCancel}
        onConfirm={confirmModalDetails.onConfirm}
        title={confirmModalDetails.title}
        message={confirmModalDetails.message}
      />
      <SetUserPasswordModal
        open={modalState === ModalState.SET_PASSWORD}
        userName={selectedUser?.userName ?? 'unknown'}
        onConfirm={async (p: string) => {
          // update the user's password
          // TODO - try catch?
          await updateUser(`/admin/user/${selectedUser?.id}`, { newPassword: p });
          await refetchUsers();
          setModalState(undefined);
        }}
        onCancel={async () => {
          // close the modal & refetch users
          await refetchUsers();
          setModalState(undefined);
        }}
      />
      <Grid size={12}>
        <DataGrid
          loading={usersLoading || groupsLoading}
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-row--selected-custom': {
              backgroundColor: 'action.selected',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            },
          }}
          disableRowSelectionOnClick
          getRowClassName={(params) =>
            selectedUser?.id === params.row.id ? 'MuiDataGrid-row--selected-custom' : ''
          }
          onRowClick={(params, e) => {
            e.preventDefault();
            // set this as the selected user
            setSelectedUser(params.row);
          }}
          columns={[
            {
              field: 'ID',
              valueGetter: (_, row) => {
                return row.id;
              },
              flex: 1,
            },
            {
              field: 'Username',
              valueGetter: (_, row) => {
                return row.userName;
              },
              flex: 1,
            },
            {
              field: "Group Name",
              valueGetter: (_, row) => {
                return row.group.name;
              },
              flex: 1,
            },
            {
              field: 'Created at',
              valueGetter: (_, row) => {
                return new Date(row.createdAt).toString();
              },
              flex: 1,
            },
            {
              field: 'Type',
              valueGetter: (_, row) => {
                if (row.acl === ACL.ADMIN) {
                  return "Admin";
                }
                return "User";
              },
              flex: 1,
            },
            {
              field: 'Status',
              valueGetter: (_, row) => {
                if (row.active) {
                  return "Active";
                }
                return "Pending";
              },
              flex: 1,
            }
          ]}
          rows={users ?? []}
        />
      </Grid>
      {selectedUser && (
        <Grid container spacing={2} size={12}>
          <Grid size={12}>
            <Typography color="primary">Actions</Typography>
          </Grid>
          <Grid size={12}>
            <Grid size={12} spacing={2} container flexDirection='row'>
              {selectedUser.acl === ACL.USER && selectedUser.group.name === "PENDING" && <Grid size={1}>
                <Button
                  color="error"
                  variant='contained'
                  onClick={() => {
                    setModalState(ModalState.CONFIRM_USER_DELETE);
                  }}
                >Delete</Button>
              </Grid>}
              {selectedUser.acl === ACL.USER && (<Grid size={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="group-select-label">Set Group</InputLabel>
                  <Select
                    labelId="group-select-label"
                    value={selectedUser.groupId}
                    onChange={async (e) => {
                      setSelectedGroupId(e.target.value);
                      setModalState(ModalState.CONFIRM_UPDATE_GROUP);
                    }}
                    label="Set user's group" // this links the floating label
                  >
                    {groups?.filter((gr) => !["ADMIN"].includes(gr.name)).map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>)}
              {selectedUser.acl === ACL.USER && (
                <Grid size={2}>
                  <Button variant="contained"
                    onClick={() => {
                      setModalState(ModalState.SET_PASSWORD);
                    }}
                  >Set Password</Button>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid size={12}>
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}